import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import SiteFooter from "../components/SiteFooter";
import { QRCodeSVG } from "qrcode.react";
import { PersonFormBlock, PersonForm, emptyPerson } from "./QuickPage";
import { useLang } from "../i18n/LanguageContext";
import { requestPay } from "../utils/pay";

/* ── 상수 ── */
const API_BASE = process.env.REACT_APP_API_URL || "";
const CUPID_PRICE = 1000;            // 참가자 1인당 결제 금액(원) — 백엔드 cupid.py CUPID_PRICE 와 일치
const ACCENT = "#D0654F";

/* ── 날짜/시간 파서 (QuickPage 로직 복제) ── */
function parseTime(val: string) {
  const s = (val || "").replace(/\s/g, "");
  if (!s) return { hour: 99, minute: 0 };
  let h: number, m: number;
  if (s.includes(":")) { const p = s.split(":"); h = parseInt(p[0]); m = parseInt(p[1] || "0"); }
  else if (/^\d{3,4}$/.test(s)) { const p = s.padStart(4, "0"); h = parseInt(p.slice(0, 2)); m = parseInt(p.slice(2)); }
  else { h = parseInt(s); m = 0; }
  return { hour: (!isNaN(h) && h >= 0 && h <= 23) ? h : 99, minute: (!isNaN(m) && m >= 0 && m <= 59) ? m : 0 };
}
function parseBirthDate(val: string) {
  const sep = val.includes("/") ? "/" : val.includes(".") ? "." : val.includes("-") ? "-" : null;
  if (sep) { const [y, m, d] = val.split(sep).map(Number); return { year: y || 0, month: m || 0, day: d || 0 }; }
  if (val.length === 8) return { year: Number(val.slice(0, 4)), month: Number(val.slice(4, 6)), day: Number(val.slice(6, 8)) };
  return { year: 0, month: 0, day: 0 };
}

/* ── 타입 ── */
interface RoomInfo {
  code: string; title: string; match_mode: string; status: string;
  free: boolean;
  count: number; participants: { name: string; gender: string }[];
  price: number; min_people: number; max_people: number; is_host?: boolean;
}

/* ── 애니메이션 ── */
const floatC = keyframes`0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)}`;
const heartBeat = keyframes`0%,100%{transform:scale(1)} 30%{transform:scale(1.25)} 60%{transform:scale(0.95)}`;
const shimmerC = keyframes`0%{background-position:200% center} 100%{background-position:-200% center}`;
const fadeUp = keyframes`from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)}`;

/* ── styled ── */
const GlobalC = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  *, *::before, *::after { box-sizing: border-box; }
  body {
    background-color:#FFD24A;
    background-image: radial-gradient(rgba(43,42,38,0.06) 1.6px, transparent 1.6px);
    background-size: 22px 22px;
  }
`;
const HeroChar = styled.img`
  width:150px; height:150px; object-fit:cover; object-position:center 30%; border-radius:50%;
  border:3px solid #2B2A26; margin:0 auto 8px; display:block;
  box-shadow: 0 5px 0 rgba(43,42,38,0.18);
  animation:${floatC} 3.6s ease-in-out infinite;
`;
const Wrap = styled.div`
  max-width: 560px; margin: 0 auto; padding: 0 0 60px;
  font-family: 'Pretendard',sans-serif; color:#2B2A26;
`;
const Hero = styled.div`
  text-align:center; padding: 22px 20px 12px;
  background:transparent; position:relative;
`;
const HeroTitle = styled.h1`
  margin: 4px 0 4px; font-size: 1.7rem; font-weight: 900; letter-spacing:-.03em; color:#2B2A26;
  text-shadow: 0 2px 0 #FFEBA6;
`;
const HeroSub = styled.p`margin:0; font-size:.88rem; color:#2B2A26; font-weight:700;`;
const Section = styled.div`padding: 22px 16px 0;`;
const RowLabel = styled.div`
  display:flex; align-items:center; justify-content:space-between; margin: 4px 2px 10px;
  font-size:.92rem; font-weight:800; color:#2B2A26;
`;
const Card = styled.div`
  background:#fff; border:2.5px solid #2B2A26; border-radius:20px; padding:18px 16px; margin-bottom:14px;
  box-shadow: 0 4px 0 rgba(43,42,38,0.18);
  animation:${fadeUp} .5s ease both;
`;
const BigBtn = styled.button`
  width:100%; padding:16px; border:2.5px solid #2B2A26; border-radius:16px; margin-top:6px;
  background: #D0654F; color:#fff; font-size:1.05rem; font-weight:900;
  cursor:pointer; box-shadow: 0 4px 0 rgba(43,42,38,0.22); transition:transform .1s, box-shadow .1s;
  &:hover{ filter:brightness(1.03); } &:active{ transform:translateY(3px); box-shadow:0 1px 0 rgba(43,42,38,0.22); }
  &:disabled{opacity:.5;cursor:not-allowed; transform:none; box-shadow:0 4px 0 rgba(43,42,38,0.22);}
`;
const GhostBtn = styled.button`
  width:100%; padding:15px; border:2.5px solid #2B2A26; border-radius:16px; margin-top:12px;
  background:#fff; color:#D0654F; font-size:1rem; font-weight:900; cursor:pointer;
  box-shadow: 0 4px 0 rgba(43,42,38,0.2); transition:transform .1s, box-shadow .1s;
  &:hover{ background:#FFF9E8; } &:active{ transform:translateY(3px); box-shadow:0 1px 0 rgba(43,42,38,0.2); }
  &:disabled{opacity:.5;cursor:not-allowed;}
`;
const SmallBtn = styled.button`
  padding:9px 14px; border:1.5px solid #2B2A26; border-radius:10px;
  background:#FBF6EA; color:#D0654F; font-size:.84rem; font-weight:800; cursor:pointer;
  &:hover{ background:#F6EAD8; } &:disabled{opacity:.5;cursor:not-allowed;}
`;
/* 상단 왼쪽 이전 버튼 — 다른 페이지 홈버튼과 동일 스타일 */
const BackLink = styled.button`
  display:inline-flex; align-items:center; gap:5px; margin-bottom:14px;
  background:rgba(255,255,255,0.7); border:1.5px solid #2B2A26; border-radius:20px;
  padding:7px 14px; color:#2B2A26; font-size:13px; font-weight:700;
  cursor:pointer; font-family:inherit;
  &:hover{ background:#FBF6EA; }
`;
/* 박스를 줄이고 여백·위계로 정리하는 요소들 */
const SecHead = styled.div`
  font-size:.8rem; font-weight:800; color:#2B2A26; letter-spacing:.03em;
  margin:0 4px 10px; text-transform:none;
`;
/* 방만들기 · 참가하기 — 양쪽 나란히 + hover 툴팁 설명 */
const PickRow = styled.div`display:flex; gap:10px; margin-top:2px;`;
const PickWrap = styled.div`flex:1; position:relative;`;
const Tip = styled.div`
  position:absolute; left:0; right:0; top:calc(100% + 9px); z-index:5;
  background:#2B2A26; color:#fff; font-size:.78rem; font-weight:600; line-height:1.5;
  padding:10px 12px; border-radius:10px; text-align:center;
  opacity:0; visibility:hidden; transform:translateY(-4px); transition:opacity .16s, transform .16s;
  pointer-events:none;
  &::before{ content:''; position:absolute; top:-5px; left:50%; margin-left:-5px;
    width:10px; height:10px; background:#2B2A26; transform:rotate(45deg); }
`;
const PickWrapHover = styled(PickWrap)`
  &:hover ${Tip}{ opacity:1; visibility:visible; transform:translateY(0); }
`;
/* 내 그룹 — 작은 알약(pill) */
const RoomChips = styled.div`display:flex; flex-wrap:wrap; gap:8px;`;
const RoomChip = styled.button`
  padding:8px 14px; border:1.5px solid #2B2A26; border-radius:999px; background:#fff; cursor:pointer;
  font-size:.82rem; font-weight:800; color:#2B2A26; font-family:inherit;
  &:hover{ background:#FBF6EA; }
`;
const TopRow = styled.div`display:flex; gap:8px; margin-bottom:16px;`;
const TopBtn = styled.button<{ $bg?: string; $fg?: string }>`
  flex:1; padding:11px 8px; border:2.5px solid #2B2A26; border-radius:14px;
  background:${p => p.$bg || "#fff"}; color:${p => p.$fg || "#2B2A26"};
  font-size:.82rem; font-weight:900; line-height:1.25; cursor:pointer; font-family:inherit;
  box-shadow: 0 3px 0 rgba(43,42,38,0.18); transition:transform .1s, box-shadow .1s;
  &:active{ transform:translateY(2px); box-shadow:0 1px 0 rgba(43,42,38,0.18); }
`;
const TextInput = styled.input`
  width:100%; padding:13px 12px; border:1.5px solid #2B2A26; border-radius:12px;
  font-size:.95rem; color:#2B2A26; background:#fff; outline:none; box-sizing:border-box;
  &:focus{ border-color:#D0654F; }
`;
const CodeInput = styled(TextInput)`
  text-align:center; font-size:1.5rem; font-weight:900; letter-spacing:.35em; text-transform:uppercase;
`;
const Label = styled.div`font-size:.85rem; font-weight:700; color:#2B2A26; margin:2px 2px 7px;`;
const ModeToggle = styled.div`
  display:flex; gap:6px; background:#F1E9D8; border-radius:999px; padding:4px; margin:2px 0 4px;
`;
const ModeBtn = styled.button<{ $on: boolean }>`
  flex:1; padding:11px 8px; border:none; border-radius:999px; font-size:.86rem; font-weight:800; cursor:pointer;
  background:${p => p.$on ? "#D0654F" : "transparent"};
  color:${p => p.$on ? "#fff" : "#2B2A26"}; transition:.15s;
`;
const CodeBadge = styled.div`
  text-align:center; font-size:2.3rem; font-weight:900; letter-spacing:.28em;
  color:#D0654F; background:#fff; border:2.5px dashed #2B2A26; border-radius:16px; padding:16px; margin:6px 0 4px;
`;
const QrBox = styled.div`
  display:flex; justify-content:center; align-items:center;
  padding:16px; background:#fff; border:2px solid #2B2A26; border-radius:16px; margin:4px 0 0;
`;
const CodeMini = styled.div`
  text-align:center; font-size:.82rem; color:#2B2A26; font-weight:700;
  letter-spacing:.12em; margin-top:10px;
`;
const ShareRow = styled.div`display:flex; gap:8px; margin-top:10px;`;
const ChipList = styled.div`display:flex; flex-wrap:wrap; gap:8px; margin-top:6px;`;
const Chip = styled.div<{ $male?: boolean }>`
  display:flex; align-items:center; gap:5px; padding:7px 12px; border-radius:999px; font-size:.86rem; font-weight:700;
  background:${p => p.$male ? "#E5EEF7" : "#FBE0D9"}; color:${p => p.$male ? "#4A7BA8" : "#D0654F"};
  border:1px solid ${p => p.$male ? "#2B2A26" : "#2B2A26"};
`;
const ErrMsg = styled.div`margin-top:10px; color:#d33; font-size:.83rem; text-align:center; font-weight:600;`;
const Note = styled.div`font-size:.8rem; color:#2B2A26; line-height:1.5; margin-top:8px;`;
const Overlay = styled.div`
  position:fixed; inset:0; z-index:999; background:rgba(40,10,30,.55);
  display:flex; flex-direction:column; align-items:center; justify-content:center; gap:14px; color:#fff;
`;
const BigHeart = styled.div`font-size:3rem; color:#C0392B; animation:${heartBeat} .9s ease-in-out infinite;`;

function genderEmoji(g: string) { return g === "male" ? "♂" : "♀"; }
const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

/* ── 휴대폰 결제 복귀 ────────────────────────────────────────────
   휴대폰 결제는 콜백이 아니라 주소 이동으로 돌아온다. 돌아오면 화면 상태가 통째로
   날아가기 때문에, 결제 전에 참가 정보를 적어두지 않으면 '결제만 되고 참가는 안 됨'이 된다.
   또 돌아올 주소에 물음표가 이미 있으면 결제번호가 물음표로 또 붙어 주소가 깨진다. */
const CUPID_PENDING = "cupidPendingJoin";

function cupidParams(): URLSearchParams {
  const raw = (typeof window === "undefined" ? "" : window.location.search || "")
    .replace(/\?/g, (_m, i) => (i === 0 ? "?" : "&"));
  return new URLSearchParams(raw);
}

function readCupidReturn() {
  if (typeof window === "undefined") return { isReturn: false } as any;
  const q = cupidParams();
  const impUid = q.get("imp_uid") || "";
  const merchantUid = q.get("merchant_uid") || "";
  const success = q.get("imp_success") ?? q.get("success");
  const isReturn = !!(impUid || success !== null);
  let saved: any = null;
  try { saved = JSON.parse(sessionStorage.getItem(CUPID_PENDING) || "null"); } catch { /* noop */ }
  if (!isReturn && saved) {
    // 결제창을 그냥 닫은 경우 — 남겨둔 기록을 지운다
    try { sessionStorage.removeItem(CUPID_PENDING); } catch { /* noop */ }
    saved = null;
  }
  return { isReturn: isReturn && !!saved, impUid, merchantUid, success, saved };
}

/* ══════════════════════════════════════════════════════════════ */
type View = "landing" | "create" | "host" | "join" | "group";

export default function CupidPage({ freeMode = false }: { freeMode?: boolean }) {
  const { lang } = useLang();
  const L = (ko: string, en: string) => (lang === "en" ? en : ko);
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  /* 결제창에서 돌아온 참인지 — 주소는 곧 정리되므로 그리기 단계에서 한 번만 읽어 붙잡아 둔다.
     useRef(읽기())로 하면 그릴 때마다 다시 읽혀서 저장해 둔 참가 정보를 엉뚱한 때 지운다. */
  const [cupidReturn] = useState(readCupidReturn);
  const urlCode = (sp.get("code") || "").toUpperCase();
  const urlHost = sp.get("host") || "";
  const basePath = freeMode ? "/cupid_new" : "/cupid";

  const [view, setView] = useState<View>("landing");
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState("");

  // 방 만들기 입력
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState<"opposite" | "all">("opposite");

  // 주최자 대시보드
  const [room, setRoom] = useState<RoomInfo | null>(null);
  const [hostToken, setHostToken] = useState("");

  // 참가하기
  const [codeInput, setCodeInput] = useState(urlCode);
  const [joinPerson, setJoinPerson] = useState<PersonForm>(emptyPerson());
  const [email, setEmail] = useState("");
  const [personToken, setPersonToken] = useState("");
  const [copied, setCopied] = useState(false);
  const [myRooms, setMyRooms] = useState<{ code: string; token: string; title: string }[]>([]);
  const [myParts, setMyParts] = useState<{ code: string; token: string; title: string; name: string }[]>([]);

  /* 내가 만든 그룹(주최) + 내가 참가한 것 목록 — localStorage 기반, 홈에서 다시 진입.
     무료(/cupid_new)와 유료(/cupid)는 현재 모드에 해당하는 것만 보이게 분리한다.
     ⚠️ 참가 기록은 방 코드당 '여러 명'이 될 수 있다(같은 기기에서 여러 명 참가). 배열로 저장·표시. */
  useEffect(() => {
    try {
      const hosts: { code: string; token: string; title: string }[] = [];
      const parts: { code: string; token: string; title: string; name: string }[] = [];
      const EXPIRE_MS = 12 * 60 * 60 * 1000;   // 12시간
      const staleKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        const isHost = k && k.startsWith("cupid_host_");
        const isPart = k && k.startsWith("cupid_part_");
        if (!isHost && !isPart) continue;
        const raw = localStorage.getItem(k!) || "";
        if (!raw) continue;

        if (isHost) {
          // 주최자: 방당 1명 (단일 객체)
          let token = raw, title = "", created = 0, free = false;
          try { const o = JSON.parse(raw); if (o && o.token) { token = o.token; title = o.title || ""; created = o.created || 0; free = !!o.free; } } catch { /* 구버전 토큰 문자열 → 유료 취급 */ }
          if (!created || Date.now() - created > EXPIRE_MS) { staleKeys.push(k!); continue; }
          if (free !== freeMode) continue;
          hosts.push({ code: k!.slice("cupid_host_".length), token, title });
          continue;
        }

        // 참가자: 방당 여러 명(배열). 구버전(단일 객체/토큰 문자열)도 흡수.
        let list: any[] = [];
        try {
          const o = JSON.parse(raw);
          if (Array.isArray(o)) list = o;
          else if (o && o.token) list = [o];
        } catch { list = [{ token: raw }]; }
        const code = k!.slice("cupid_part_".length);
        const kept: any[] = [];
        for (const e of list) {
          if (!e || !e.token) continue;
          const created = e.created || 0;
          if (!created || Date.now() - created > EXPIRE_MS) continue;   // 만료 항목 제거
          kept.push(e);
          if (!!e.free !== freeMode) continue;                          // 현재 모드와 다른 건 숨김(저장은 유지)
          parts.push({ code, token: e.token, title: e.title || "", name: e.name || "" });
        }
        if (kept.length === 0) staleKeys.push(k!);
        else if (kept.length !== list.length) { try { localStorage.setItem(k!, JSON.stringify(kept)); } catch { /* noop */ } }
      }
      staleKeys.forEach(k => { try { localStorage.removeItem(k); } catch { /* noop */ } });
      setMyRooms(hosts.reverse());
      setMyParts(parts.reverse());
    } catch { /* noop */ }
  }, [view, freeMode]);

  /* 방 정보 로드 */
  const loadRoom = useCallback(async (code: string, ht?: string) => {
    const res = await axios.get(`${API_BASE}/api/v1/cupid/room/${code}`,
      { params: ht ? { host_token: ht } : {} });
    return res.data as RoomInfo;
  }, []);

  /* 휴대폰으로 참가비를 결제하고 돌아왔을 때 — 결제만 되고 참가가 안 되는 걸 막는다.
     결제창은 콜백 없이 주소 이동으로 돌아오기 때문에, 여기서 적어둔 정보로 참가를 마무리한다. */
  useEffect(() => {
    if (!cupidReturn.isReturn) return;
    const { impUid, merchantUid, success, saved } = cupidReturn;
    try { sessionStorage.removeItem(CUPID_PENDING); } catch { /* noop */ }
    if (success === "false" || success === "0") {
      setErr(L("결제가 취소되었습니다.", "Payment was cancelled."));
      return;
    }
    if (!saved?.code || !saved?.person) return;
    (async () => {
      setPaying(true); setErr("");
      try {
        const r = await loadRoom(saved.code);
        setRoom(r); setCodeInput(saved.code); setView("join");
        setJoinPerson(saved.person);
        if (saved.email) setEmail(saved.email);
        const res = await axios.post(
          `${API_BASE}/api/v1/cupid/room/${saved.code}/join`,
          buildJoinPayload(saved.person, impUid, merchantUid || saved.merchantUid),
        );
        setPersonToken(res.data.person_token);
        rememberPart(saved.code, res.data.person_token,
                     (saved.person.name || "").trim(), r.title || "");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e: any) {
        // 결제는 끝난 상태다 — 다시 결제하라고 하면 안 된다.
        setErr((e?.response?.data?.detail ||
                L("참가 처리에 실패했습니다.", "Failed to join.")) +
               L(` (결제는 정상 처리됐습니다. 주문번호 ${merchantUid || saved.merchantUid || ""} 로 문의해 주세요)`,
                 ` (Your payment went through. Please contact us with order no. ${merchantUid || saved.merchantUid || ""}.)`));
      } finally { setPaying(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* URL 진입 처리: ?code=&host= → 주최자, ?code= → 참가 */
  useEffect(() => {
    (async () => {
      if (!urlCode) return;
      try {
        const r = await loadRoom(urlCode, urlHost || undefined);
        if (urlHost && r.is_host) {
          setRoom(r); setHostToken(urlHost); setView("host");
        } else {
          setCodeInput(urlCode); setView("join");
          setRoom(r);
        }
      } catch {
        setErr(L("방을 찾을 수 없어요. 코드를 확인해주세요.", "Room not found. Please check the code."));
        setView("join");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCode, urlHost]);

  /* 대시보드/참가자 현황 폴링(참가자·마감 여부 실시간 반영) */
  useEffect(() => {
    if ((view !== "host" && view !== "group") || !room || room.status === "closed") return;
    const id = setInterval(async () => {
      // 참가자 현황(group)은 host_token 없이 공개 정보만 갱신
      try { setRoom(await loadRoom(room.code, view === "host" ? hostToken : undefined)); } catch { /* noop */ }
    }, 5000);
    return () => clearInterval(id);
  }, [view, room, hostToken, loadRoom]);

  /* ── 방 만들기 ── */
  async function handleCreate() {
    setErr(""); setBusy(true);
    try {
      const res = await axios.post(`${API_BASE}/api/v1/cupid/room/create`,
        { title: title.trim(), match_mode: mode, free: freeMode });
      const { code, host_token } = res.data;
      try { localStorage.setItem(`cupid_host_${code}`, JSON.stringify({ token: host_token, title: title.trim(), created: Date.now(), free: freeMode })); } catch { /* noop */ }
      const r = await loadRoom(code, host_token);
      setRoom(r); setHostToken(host_token); setView("host");
      navigate(`${basePath}?code=${code}&host=${host_token}`, { replace: true });
    } catch (e: any) {
      setErr(e?.response?.data?.detail || L("방 만들기에 실패했습니다.", "Failed to create the room."));
    } finally { setBusy(false); }
  }

  /* ── 방 입장(코드 확인) ── */
  async function handleEnterCode() {
    const code = codeInput.trim().toUpperCase();
    if (code.length < 4) { setErr(L("코드를 정확히 입력해주세요.", "Please enter the code correctly.")); return; }
    setErr(""); setBusy(true);
    try {
      const r = await loadRoom(code);
      setRoom(r); setCodeInput(code);
      navigate(`${basePath}?code=${code}`, { replace: true });
    } catch (e: any) {
      setErr(e?.response?.data?.detail || L("존재하지 않는 방 코드입니다.", "That room code doesn't exist."));
    } finally { setBusy(false); }
  }

  /* ── 참가 결제·제출 ── */
  /* 참가 정보를 서버가 받는 형태로 만든다.
     결제하고 돌아왔을 때는 화면 상태가 날아가 있어서, 적어둔 값을 그대로 넣어 부른다. */
  function buildJoinPayload(person: PersonForm, impUid?: string, merchantUid?: string) {
    const d = parseBirthDate(person.birth_date);
    const t = parseTime(person.birth_time);
    return {
      name: person.name.trim(), gender: person.gender,
      birth_year: d.year, birth_month: d.month, birth_day: d.day,
      birth_hour: person.birth_time_unknown ? 99 : t.hour,
      birth_minute: person.birth_time_unknown ? 0 : t.minute,
      birth_type: person.birth_type, city: person.city,
      imp_uid: impUid, merchant_uid: merchantUid,
    };
  }
  function joinPayload(impUid?: string, merchantUid?: string) {
    return buildJoinPayload(joinPerson, impUid, merchantUid);
  }

  /* 참가 기록 저장 — 방당 여러 명 누적(배열). 같은 기기에서 여러 명 참가해도 각자 결과로 재진입 가능. */
  function rememberPart(code: string, token: string, name: string, title: string) {
    try {
      const key = `cupid_part_${code}`;
      let arr: any[] = [];
      try {
        const ex = JSON.parse(localStorage.getItem(key) || "[]");
        arr = Array.isArray(ex) ? ex : (ex && ex.token ? [ex] : []);
      } catch { /* noop */ }
      if (!arr.some(e => e && e.token === token)) {
        arr.push({ token, name, title: title || "", created: Date.now(), free: freeMode });
      }
      localStorage.setItem(key, JSON.stringify(arr));
    } catch { /* noop */ }
  }
  async function doJoin(impUid?: string, merchantUid?: string) {
    if (!room) return;
    setPaying(true); setErr("");
    try {
      const res = await axios.post(`${API_BASE}/api/v1/cupid/room/${room.code}/join`, joinPayload(impUid, merchantUid));
      setPersonToken(res.data.person_token);
      rememberPart(room.code, res.data.person_token, joinPerson.name.trim(), room.title || "");
      try { sessionStorage.removeItem(CUPID_PENDING); } catch { /* noop */ }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e: any) {
      setErr(e?.response?.data?.detail || L("참가 처리에 실패했습니다.", "Failed to join."));
    } finally { setPaying(false); }
  }
  function validateJoin(): string {
    if (!joinPerson.name.trim()) return L("이름을 입력해주세요.", "Please enter your name.");
    const d = parseBirthDate(joinPerson.birth_date);
    if (!d.year || !d.month || !d.day) return L("생년월일을 확인해주세요. (예: 1990/05/15)", "Please check your birth date. (e.g. 1990/05/15)");
    return "";
  }
  async function handlePay() {
    const v = validateJoin();
    if (v) { setErr(v); return; }
    setErr("");
    if (room?.free) { doJoin(); return; }   // 무료 방: 결제 없이 바로 참가
    const IMP = (window as any).IMP;
    if (!IMP) { doJoin(); return; }   // 결제모듈 없으면 바로 참가(개발용)

    /* 결제창을 열기 전에 방이 아직 받을 수 있는지 먼저 확인한다.
       예전에는 결제부터 하고 서버에서 "이미 마감된 방입니다"로 거절당했다 —
       돈은 나가고 참가는 안 되는 상황이라 반드시 결제 앞에서 막아야 한다. */
    if (room?.code) {
      setBusy(true);
      try {
        const fresh = await loadRoom(room.code);
        setRoom(fresh);
        if (fresh.status !== "open") {
          setErr(L("이미 마감된 방입니다. 참가할 수 없어요. (결제되지 않았습니다)",
                   "This room is already closed. (You have not been charged.)"));
          return;
        }
        if (fresh.count >= fresh.max_people) {
          setErr(L(`정원(${fresh.max_people}명)이 가득 찼습니다. (결제되지 않았습니다)`,
                   `The room is full (${fresh.max_people}). (You have not been charged.)`));
          return;
        }
      } catch {
        setErr(L("지금은 참가할 수 없어요. 잠시 후 다시 시도해주세요. (결제되지 않았습니다)",
                 "Can't join right now. Please try again shortly. (You have not been charged.)"));
        return;
      } finally { setBusy(false); }
    }

    const merchantUid = `cupid_${Date.now()}`;
    // 휴대폰 결제는 주소 이동으로 돌아와 화면 상태가 날아간다 — 참가 정보를 먼저 적어둔다.
    try {
      sessionStorage.setItem(CUPID_PENDING, JSON.stringify({
        code: room?.code || "", person: joinPerson, email, merchantUid, free: freeMode,
      }));
    } catch { /* 저장 실패해도 PC 결제는 정상 동작 */ }
    setPaying(true);
    requestPay({
      merchantUid,
      name: L(`우연의 화살 참가 ${room?.title || room?.code || ""}`, `Wooyeon Arrow entry ${room?.title || room?.code || ""}`),
      amount: CUPID_PRICE,
      buyerName: joinPerson.name.trim(),
      buyerEmail: email.trim(),
      // 돌아올 주소에 물음표를 달지 않는다 — 결제번호가 또 붙으면 주소가 깨져서 못 읽는다.
      // 어느 방이었는지는 위에 적어둔 값으로 안다.
      redirectUrl: `${window.location.origin}${basePath}`,
    }).then((rsp) => {
      if (rsp.success) doJoin(rsp.imp_uid, rsp.merchant_uid);
      else { setPaying(false); setErr(rsp.error_msg || L("결제가 취소되었습니다.", "Payment was cancelled.")); }
    });
  }

  /* ── 주최자 마감 ── */
  async function handleClose() {
    if (!room) return;
    if (!window.confirm(L("마감하면 더 이상 참가할 수 없고 결과가 확정됩니다. 마감할까요?", "Once closed, no one else can join and the results are finalized. Close now?"))) return;
    setErr(""); setBusy(true);
    try {
      await axios.post(`${API_BASE}/api/v1/cupid/room/${room.code}/close`, { host_token: hostToken });
      // 이동하지 않고 방 상태만 '마감'으로 갱신 → 버튼이 "결과 보기"로 바뀜(2단계)
      const r = await loadRoom(room.code, hostToken);
      setRoom(r);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || L("마감에 실패했습니다.", "Failed to close the room."));
    } finally { setBusy(false); }
  }

  /* 큐피드 홈(랜딩)으로 — 상태를 초기화하고 쿼리 제거 */
  function goHome() {
    setErr(""); setRoom(null); setHostToken(""); setPersonToken("");
    setCodeInput(""); setJoinPerson(emptyPerson()); setView("landing");
    navigate(basePath, { replace: true });
  }

  function copy(text: string) {
    try {
      navigator.clipboard.writeText(text);
      setCopied(true); setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  }

  /* ══════════ 참가 완료 화면 ══════════ */
  if (personToken && view !== "group") {
    return (
      <Wrap>
        <GlobalC />
        <Hero>
          <HeroChar src="/woo_02.webp" alt="우연" />
          <HeroTitle>{L("참가 완료!", "You're in!")}</HeroTitle>
          <HeroSub>{L("주최자가 마감하면 나의 짝을 볼 수 있어요", "Once the host closes the room, you'll see your match")}</HeroSub>
        </Hero>
        <Section>
          <BackLink onClick={goHome}>{L("← 이전", "← Back")}</BackLink>
          <Note style={{ textAlign: "center", marginTop: 0, marginBottom: 18 }}>
            {L("주최자가 마감하면 나의 최고의 짝이 나타납니다.", "Once the host closes the room, your best match appears.")}
          </Note>
          <BigBtn onClick={() => navigate(`/cupid_r?person=${personToken}`)}>{L("♥ 내 결과 보기", "♥ See my result")}</BigBtn>
          <GhostBtn onClick={() => {
            // 주최자가 '나도 참가'로 참가 완료했으면 주최자 대시보드로, 일반 참가자는 참가자 현황(읽기 전용)으로.
            if (hostToken && room) { setPersonToken(""); setView("host"); }
            else if (room) { setView("group"); }
            else goHome();
          }}>{L("참가자 현황 보기", "See participants")}</GhostBtn>
        </Section>
        <SiteFooter />
      </Wrap>
    );
  }

  /* ══════════ 참가자 현황(읽기 전용) — QR·참가자 목록만, 마감 버튼 없음 ══════════ */
  if (view === "group" && room) {
    const shareLink = `${origin()}${basePath}?code=${room.code}`;
    const males = room.participants.filter(p => p.gender === "male");
    const females = room.participants.filter(p => p.gender !== "male");
    const closed = room.status === "closed";
    return (
      <Wrap>
        <GlobalC />
        <Hero>
          <HeroChar src="/woo_02.webp" alt="우연" />
          <HeroTitle>{room.title || L("우연의 화살", "Wooyeon's Arrow")}</HeroTitle>
          <HeroSub>{closed
            ? L("마감되었어요 · 나의 짝을 확인하세요", "Closed · check your match")
            : L("주최자가 마감하면 나의 짝이 나타나요", "Your match appears once the host closes the room")}</HeroSub>
        </Hero>
        <Section>
          <TopRow>
            <TopBtn onClick={() => setView("landing")}>{L("← 이전", "← Back")}</TopBtn>
            <TopBtn $bg="#FBE0D9" $fg="#D0654F" onClick={() => copy(shareLink)}>{copied ? L("복사됨 ✓", "Copied ✓") : L("참가 링크 공유", "Share link")}</TopBtn>
          </TopRow>
          <Card>
            <Label>{L("참가 QR · 친구에게 공유하세요", "Join QR · share with friends")}</Label>
            <QrBox>
              <QRCodeSVG value={shareLink} size={190} bgColor="#ffffff" fgColor="#2B2A26" level="M" />
            </QrBox>
            <CodeMini>{L("코드", "Code")} · {room.code}</CodeMini>
          </Card>

          <RowLabel>
            <span>{L(`참가자 (${room.count}명)`, `Participants (${room.count})`)}</span>
            <span style={{ fontSize: ".76rem", color: "#2B2A26", fontWeight: 600 }}>
              {L(`최소 ${room.min_people}명 · 최대 ${room.max_people}명`, `Min ${room.min_people} · Max ${room.max_people}`)}
            </span>
          </RowLabel>
          <Card>
            {room.count === 0 && <Note style={{ textAlign: "center", margin: "6px 0" }}>{L("아직 참가자가 없어요.", "No participants yet.")}</Note>}
            <ChipList>
              {males.map((p, i) => <Chip key={`m${i}`} $male>{genderEmoji(p.gender)} {p.name}</Chip>)}
              {females.map((p, i) => <Chip key={`f${i}`}>{genderEmoji(p.gender)} {p.name}</Chip>)}
            </ChipList>
          </Card>

          {personToken && (
            <BigBtn onClick={() => navigate(`/cupid_r?person=${personToken}`)}>{L("♥ 내 결과 보기", "♥ See my result")}</BigBtn>
          )}
          <Note style={{ textAlign: "center" }}>
            {closed
              ? L("마감되었습니다. '내 결과 보기'에서 나의 짝을 확인하세요.", "Closed. Tap 'See my result' to view your match.")
              : L("마감은 주최자만 할 수 있어요. 마감되면 여기서 결과를 볼 수 있어요.", "Only the host can close the room. Results appear once it's closed.")}
          </Note>
          {err && <ErrMsg>{err}</ErrMsg>}
        </Section>
        <SiteFooter />
      </Wrap>
    );
  }

  /* ══════════ 주최자 대시보드 ══════════ */
  if (view === "host" && room) {
    const shareLink = `${origin()}${basePath}?code=${room.code}`;
    const males = room.participants.filter(p => p.gender === "male");
    const females = room.participants.filter(p => p.gender !== "male");
    return (
      <Wrap>
        <GlobalC />
        <Hero>
          <HeroChar src="/woo_02.webp" alt="우연" />
          <HeroTitle>{room.title || L("우연의 화살", "Wooyeon's Arrow")}</HeroTitle>
          <HeroSub>{L("친구들에게 코드를 공유하세요", "Share the code with your friends")} · {room.match_mode === "all" ? L("동성 포함", "Same-sex included") : L("이성 궁합만", "Opposite-sex only")}</HeroSub>
        </Hero>
        <Section>
          <TopRow>
            <TopBtn onClick={goHome}>{L("← 홈", "← Home")}</TopBtn>
            {room.status !== "closed" && (
              <TopBtn $bg="#E5EEF7" $fg="#4A7BA8" onClick={() => { setJoinPerson(emptyPerson()); setView("join"); }}>{L("나도 참가자로 입력하기", "Join as a participant")}</TopBtn>
            )}
            <TopBtn $bg="#FBE0D9" $fg="#D0654F" onClick={() => copy(shareLink)}>{copied ? L("복사됨 ✓", "Copied ✓") : L("참가 링크", "Join link")}</TopBtn>
          </TopRow>
          <Card>
            <Label>{L("참가 QR · 스캔하면 바로 입장돼요", "Join QR · scan to enter instantly")}</Label>
            <QrBox>
              <QRCodeSVG value={shareLink} size={190} bgColor="#ffffff" fgColor="#2B2A26" level="M" />
            </QrBox>
          </Card>

          <RowLabel>
            <span>{L(`참가자 (${room.count}명)`, `Participants (${room.count})`)}</span>
            <span style={{ fontSize: ".76rem", color: "#2B2A26", fontWeight: 600 }}>
              {L(`최소 ${room.min_people}명 · 최대 ${room.max_people}명`, `Min ${room.min_people} · Max ${room.max_people}`)}
            </span>
          </RowLabel>
          <Card>
            {room.count === 0 && <Note style={{ textAlign: "center", margin: "6px 0" }}>{L("아직 참가자가 없어요. 코드를 공유해보세요!", "No participants yet. Share the code!")}</Note>}
            <ChipList>
              {males.map((p, i) => <Chip key={`m${i}`} $male>{genderEmoji(p.gender)} {p.name}</Chip>)}
              {females.map((p, i) => <Chip key={`f${i}`}>{genderEmoji(p.gender)} {p.name}</Chip>)}
            </ChipList>
          </Card>

          {room.status === "closed" ? (
            <>
              <BigBtn onClick={() => navigate(`/cupid_r?code=${room.code}`)}>{L("♥ 결과 보기", "♥ See results")}</BigBtn>
              <Note style={{ textAlign: "center" }}>{L("마감되었습니다. 참가자들도 각자 링크에서 결과를 볼 수 있어요.", "Closed. Participants can also see results from their own links.")}</Note>
            </>
          ) : (
            <>
              <BigBtn onClick={handleClose} disabled={busy || room.count < room.min_people}>
                {L("마감하고 결과 보기", "Close & see results")}
              </BigBtn>
              {room.count < room.min_people &&
                <Note style={{ textAlign: "center" }}>{L(`최소 ${room.min_people}명이 모이면 마감할 수 있어요.`, `You can close once at least ${room.min_people} people join.`)}</Note>}
            </>
          )}
          {err && <ErrMsg>{err}</ErrMsg>}
        </Section>
        <SiteFooter />
      </Wrap>
    );
  }

  /* ══════════ 참가하기 (코드 입력 or 사주 입력) ══════════ */
  if (view === "join") {
    // 방 정보가 이미 로드돼 있으면(주최자가 '나도 참가' or QR/링크로 진입) 코드 입력을 건너뛰고 바로 사주 입력
    const entered = !!room;
    return (
      <Wrap>
        <GlobalC />
        {paying && <Overlay><BigHeart>♥</BigHeart><div style={{ fontWeight: 800 }}>{L("참가 처리 중…", "Joining…")}</div></Overlay>}
        <Hero>
          <HeroChar src="/woo_02.webp" alt="우연" />
          <HeroTitle>{L("우연의 화살 참가", "Join Wooyeon's Arrow")}</HeroTitle>
          <HeroSub>{entered ? (room!.title || L("내 사주를 입력하세요", "Enter your birth info")) : L("방 코드를 입력하세요", "Enter the room code")}</HeroSub>
        </Hero>
        <Section>
          <BackLink onClick={() => { setErr(""); if (hostToken && room) { setView("host"); } else { goHome(); } }}>{L("← 이전", "← Back")}</BackLink>
          {!entered ? (
            <>
              <Card>
                <Label>{L("방 코드", "Room code")}</Label>
                <CodeInput placeholder="ABCDE" value={codeInput}
                  onChange={e => setCodeInput(e.target.value.toUpperCase())} maxLength={6} />
                <BigBtn style={{ marginTop: 14 }} onClick={handleEnterCode} disabled={busy}>{L("입장하기", "Enter")}</BigBtn>
              </Card>
              <Note style={{ textAlign: "center" }}>
                {freeMode
                  ? L("주최자에게 받은 코드로 입장한 뒤, 내 사주를 입력하면 바로 참가됩니다.", "Enter with the code from your host, fill in your birth info, and you're in.")
                  : L("주최자에게 받은 코드로 입장한 뒤, 내 사주를 입력하고 결제하면 참가됩니다.", "Enter with the code from your host, fill in your birth info, and pay to join.")}
              </Note>
            </>
          ) : (
            <>
              <RowLabel><span>{room!.title || L("내 사주 입력", "Enter your birth info")}</span>
                <span style={{ fontSize: ".76rem", color: "#2B2A26", fontWeight: 600 }}>{L(`현재 ${room!.count}명 참가`, `${room!.count} joined so far`)}</span>
              </RowLabel>
              <PersonFormBlock title={L("내 정보", "My info")} data={joinPerson} onChange={setJoinPerson} accentColor={ACCENT} />
              <div style={{ marginTop: 4 }}>
                <Label>{L("이메일 (선택)", "Email (optional)")}</Label>
                <TextInput type="email" placeholder="example@email.com" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <BigBtn style={{ marginTop: 16 }} onClick={handlePay} disabled={paying}>
                {room!.free ? L("♥ 바로 참가하기", "♥ Join now") : L(`♥ ${CUPID_PRICE.toLocaleString()}원 결제하고 참가하기`, `♥ Pay ₩${CUPID_PRICE.toLocaleString()} & join`)}
              </BigBtn>
              <Note style={{ textAlign: "center" }}>
                {room!.free
                  ? L("바로 참가할 수 있어요. 참가 후 개인 결과 링크가 발급됩니다.", "You can join right away. A personal result link is issued after joining.")
                  : L(`참가비는 1인당 ${CUPID_PRICE.toLocaleString()}원입니다. 결제 후 개인 결과 링크가 발급돼요.`, `The entry fee is ₩${CUPID_PRICE.toLocaleString()} per person. A personal result link is issued after payment.`)}
              </Note>
            </>
          )}
          {err && <ErrMsg>{err}</ErrMsg>}
        </Section>
        <SiteFooter />
      </Wrap>
    );
  }

  /* ══════════ 방 만들기 ══════════ */
  if (view === "create") {
    return (
      <Wrap>
        <GlobalC />
        <Hero>
          <HeroChar src="/woo_02.webp" alt="우연" />
          <HeroTitle>{L("우연의 화살 방 만들기", "Create a Wooyeon's Arrow room")}</HeroTitle>
          <HeroSub>{L("제목과 매칭 방식을 정하면 코드가 발급돼요", "Set a title and matching mode to get a code")}</HeroSub>
        </Hero>
        <Section>
          <Card>
            <Label>{L("모임 제목", "Group title")}</Label>
            <TextInput placeholder={L("예) 우리 동아리 궁합 대회", "e.g. Our club compatibility contest")} value={title}
              onChange={e => setTitle(e.target.value)} maxLength={30} />
            <div style={{ height: 16 }} />
            <Label>{L("매칭 방식", "Matching mode")}</Label>
            <ModeToggle>
              <ModeBtn $on={mode === "opposite"} onClick={() => setMode("opposite")}>{L("이성 궁합만", "Opposite-sex only")}</ModeBtn>
              <ModeBtn $on={mode === "all"} onClick={() => setMode("all")}>{L("동성 포함", "Same-sex included")}</ModeBtn>
            </ModeToggle>
            <Note>{mode === "opposite"
              ? L("남녀 사이의 궁합만 계산해 짝을 이어줍니다.", "Only male–female compatibility is calculated to match pairs.")
              : L("성별 관계없이 모든 사람 사이의 궁합을 계산합니다.", "Compatibility is calculated between everyone, regardless of gender.")}</Note>
          </Card>
          <BigBtn onClick={handleCreate} disabled={busy}>{L("방 만들고 코드 받기 →", "Create room & get code →")}</BigBtn>
          <GhostBtn onClick={() => { setView("landing"); setErr(""); }}>{L("← 뒤로", "← Back")}</GhostBtn>
          {err && <ErrMsg>{err}</ErrMsg>}
        </Section>
        <SiteFooter />
      </Wrap>
    );
  }

  /* ══════════ 랜딩 ══════════ */
  return (
    <Wrap>
      <GlobalC />
      <Hero>
        <HeroChar src="/woo_02.webp" alt="우연" />
        <HeroTitle>{L("우연의 화살", "Wooyeon's Arrow")}</HeroTitle>
        <HeroSub>{L("여러 명이 각자 사주를 넣으면 · 가장 잘 맞는 짝을 이어드려요", "Everyone enters their birth info · we link the best-matched pairs")}</HeroSub>
      </Hero>
      <Section>
        <BackLink onClick={() => navigate("/")}>{L("← 홈", "← Home")}</BackLink>
        {myParts.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <SecHead>{L("내 참가 · 결과/현황 보기", "My entries · see result/status")}</SecHead>
            <RoomChips>
              {myParts.map(r => (
                <RoomChip key={r.token} style={{ background: "#FFF4CC", borderColor: "#D0654F", color: "#D0654F" }}
                  onClick={() => navigate(`/cupid_r?person=${r.token}`)}>
                  ♥ {r.name || r.title || r.code}{r.name && (r.title || r.code) ? ` · ${r.title || r.code}` : ""}
                </RoomChip>
              ))}
            </RoomChips>
          </div>
        )}
        {myRooms.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <SecHead>{L("내 그룹 · 다시 입장", "My groups · re-enter")}</SecHead>
            <RoomChips>
              {myRooms.map(r => (
                <RoomChip key={r.code}
                  onClick={() => navigate(`${basePath}?code=${r.code}&host=${r.token}`)}>
                  {r.title || r.code}
                </RoomChip>
              ))}
            </RoomChips>
          </div>
        )}
        <BigBtn onClick={() => { setView("create"); setErr(""); }}>{L("방 만들기", "Create a room")}</BigBtn>
        <Note style={{ textAlign: "center" }}>
          {freeMode
            ? L("방을 만들고 QR을 친구들에게 공유하세요. 친구들은 QR을 스캔하면 코드 입력 없이 바로 참가해요.", "Create a room and share the QR with friends. They scan it and join instantly — no code needed.")
            : L(`방을 만들고 QR을 친구들에게 공유하세요. 친구들은 QR을 스캔해 바로 입장하고, 1인당 ${CUPID_PRICE.toLocaleString()}원 결제하면 참가됩니다.`, `Create a room and share the QR with friends. They scan to enter and join by paying ₩${CUPID_PRICE.toLocaleString()} each.`)}
        </Note>
      </Section>
      <SiteFooter />
    </Wrap>
  );
}
